import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { SuperHead } from "@/pages/super/SuperLayout";
import { planLimits } from "@/pages/Pricing";
import { api, gbp, formatApiError } from "@/lib/api";

const EMPTY = { name: "", price: 29, description: "", features: [], max_practitioners: "", max_services: "", featured: false, platform_fee_percent: 0, active: true, sort: 0 };

const Num = ({ label, value, onChange, testid, hint }) => (
  <div className="space-y-1.5">
    <Label>{label}</Label>
    <Input type="number" min={0} step="any" value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

const PlanDialog = ({ open, initial, onClose, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  useEffect(() => { setF(initial ? { ...EMPTY, ...initial } : EMPTY); }, [initial, open]);
  const save = async () => {
    const opt = (v) => (v === "" || v == null ? null : Number(v));
    const body = { ...f, price: Number(f.price), platform_fee_percent: Number(f.platform_fee_percent || 0), sort: Number(f.sort || 0),
      max_practitioners: opt(f.max_practitioners), max_services: opt(f.max_services),
      features: (typeof f.features === "string" ? f.features.split("\n") : f.features).map((x) => x.trim()).filter(Boolean) };
    delete body.id; delete body.created_at; delete body.clinic_count;
    try {
      if (initial?.id) await api.put(`/super/plans/${initial.id}`, body); else await api.post("/super/plans", body);
      toast.success("Plan saved"); onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const feats = typeof f.features === "string" ? f.features : (f.features || []).join("\n");
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="plan-dialog">
        <DialogHeader><DialogTitle>{initial?.id ? "Edit plan" : "New plan"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5 sm:col-span-2"><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="plan-name-input" /></div>
          <Num label="Price per month (£)" value={f.price} onChange={(v) => setF({ ...f, price: v })} testid="plan-price-input" />
          <Num label="Platform fee per booking (%, invoiced monthly)" value={f.platform_fee_percent} onChange={(v) => setF({ ...f, platform_fee_percent: v })} testid="plan-fee-input" />
          <Num label="Max practitioners" value={f.max_practitioners} onChange={(v) => setF({ ...f, max_practitioners: v })} testid="plan-max-practitioners-input" hint="Leave empty for unlimited" />
          <Num label="Max treatments" value={f.max_services} onChange={(v) => setF({ ...f, max_services: v })} testid="plan-max-services-input" hint="Leave empty for unlimited" />
          <Num label="Display order" value={f.sort} onChange={(v) => setF({ ...f, sort: v })} testid="plan-sort-input" />
          <div className="space-y-1.5 sm:col-span-2"><Label>Short description</Label><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="plan-description-input" /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Extra features (one per line)</Label><Textarea rows={4} value={feats} onChange={(e) => setF({ ...f, features: e.target.value })} data-testid="plan-features-input" /></div>
        </div>
        <label className="flex items-center gap-3 text-sm"><Switch checked={f.featured} onCheckedChange={(v) => setF({ ...f, featured: v })} data-testid="plan-featured-switch" />Featured spot on the homepage</label>
        <label className="flex items-center gap-3 text-sm"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="plan-active-switch" />Available to new clinics</label>
        <p className="text-xs text-muted-foreground">Price changes apply to new subscriptions and plan switches. Existing subscribers keep their current price until they change plan.</p>
        <DialogFooter><Button onClick={save} disabled={!f.name} data-testid="plan-save-button">Save plan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function SuperPlans() {
  const [plans, setPlans] = useState([]);
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/super/plans").then((r) => setPlans(r.data));
  useEffect(() => { load(); }, []);
  const remove = async (p) => {
    if (!window.confirm(`Remove the ${p.name} plan?`)) return;
    try { const { data } = await api.delete(`/super/plans/${p.id}`); toast.success(data.archived ? "Plan is in use, so it was hidden from new clinics" : "Plan deleted"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <>
      <SuperHead eyebrow="Monetisation" title="Plans & pricing">
        <Button className="rounded-full" onClick={() => { setEdit(null); setOpen(true); }} data-testid="add-plan-button"><Plus className="h-4 w-4 mr-1.5" />Add plan</Button>
      </SuperHead>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
        {plans.map((p) => (
          <div key={p.id} className="rounded-2xl bg-card border border-border p-6" data-testid={`super-plan-card-${p.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-display font-bold text-lg">{p.name} {!p.active && <span className="text-xs font-normal text-muted-foreground">(hidden)</span>}</p>
                <p className="font-display text-2xl font-extrabold">{gbp(p.price)}<span className="text-sm font-normal text-muted-foreground"> /month</span></p>
              </div>
              <div className="flex">
                <Button variant="ghost" size="icon" onClick={() => { setEdit(p); setOpen(true); }} data-testid={`edit-plan-button-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => remove(p)} data-testid={`delete-plan-button-${p.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
              </div>
            </div>
            <ul className="mt-4 text-sm space-y-1 text-slate-600">{planLimits(p).map((l) => <li key={l}>• {l}</li>)}</ul>
            <p className="text-xs text-muted-foreground mt-4">{p.clinic_count} clinics on this plan</p>
          </div>
        ))}
      </div>
      <PlanDialog open={open} initial={edit} onClose={() => setOpen(false)} onSaved={load} />
    </>
  );
}
